import { GAMES_NAMES } from "./enums.utils";

/**
 * Validates game ID based on game name requirements
 * @param {string} value - The game ID to validate
 * @param {string} gameName - The name of the game from GAMES_NAMES enum
 * @returns {string} - Error message if validation fails, empty string if validation passes
 */
export const validateInGameId = (value, gameName) => {
  if (!value) return "Game ID is required";

  let error = "";
  const regExPattern = /[a-zA-Z !@#$%^&*()_+<>?:{}|;,./`~]/g;
  const result = value.match(regExPattern);
  const freeFireRegEx = /[a-zA-Z !@#$%^&*()_+<>?:{}|;,./`~]/g;
  const freeFireResult = value.match(freeFireRegEx);
  const codRegEx = /[a-zA-Z !@#$%^&*()_+<>?:{}|;,./`~]/g;
  const codResult = value.match(codRegEx);
  const fortniteRegEx = /[!@#$%^&*()_+<>?:{}|;,./`~]/g;
  const fortniteResult = value.match(fortniteRegEx);
  const pubgMobileRegEx = /[a-zA-Z !@#$%^&*()_+<>?:{}|;,./`~]/g;
  const pubgMobileResult = value.match(pubgMobileRegEx);
  const apexRegEx = /[!@#$%^&*()_+<>?:{}|;,./`~]/g;
  const apexResult = value.match(apexRegEx);
  const bgmiRegEx = /[!@#$%^&*()_+<>?:{}|;,./`~]/g;
  const bgmiResult = value.match(bgmiRegEx);

  switch (gameName) {
    case GAMES_NAMES.LEAGUE_OF_LEGENDS:
    case GAMES_NAMES.VALORANT:
      if (value.search("#") === -1) {
        error = "In-Game ID should be in RIOTID#TAGLINE format.";
      }
      break;

    case GAMES_NAMES.DOTA_2:
    case GAMES_NAMES.CS_GO:
    case GAMES_NAMES.PUBG:
      if (result !== null) {
        error = "In-Game ID should contain just numeric values.";
      } else if (value.length !== 17) {
        error = "In-Game ID should contain 17 numeric characters.";
      }
      break;

    case GAMES_NAMES.FREEFIRE:
      if (freeFireResult !== null) {
        error = "In-Game ID should contain just numeric values.";
      } else if (value.length < 8 || value.length > 12) {
        error = "In-Game ID should be 8 to 12 numeric characters.";
      }
      break;

    case GAMES_NAMES.CALL_OF_DUTY:
      if (codResult !== null) {
        error = "In-Game ID should contain just numeric values.";
      } else if (value.length !== 19) {
        error = "In-Game ID should contain 19 numeric characters.";
      }
      break;

    case GAMES_NAMES.FORTNITE:
      if (fortniteResult !== null) {
        error = "In-Game ID should contain just alphanumeric characters.";
      } else if (value.length !== 32) {
        error = "In-Game ID should contain 32 alphanumeric characters.";
      }
      break;

    case GAMES_NAMES.PUBG_MOBILE:
      if (pubgMobileResult !== null) {
        error = "In-Game ID should contain just numeric values.";
      }
      break;

    case GAMES_NAMES.APEX_LEGENDS:
      if (apexResult !== null) {
        error = "In-Game ID should contain just alpha-numerics with upper cases.";
      } else if (value.length !== 20) {
        error = "In-Game ID should contain 20 alphanumeric characters.";
      }
      break;

    case GAMES_NAMES.BGMI_TEAM_FFA:
      if (bgmiResult !== null) {
        error = "In-Game ID should contain just numeric values.";
      } else if (value.length < 8 || value.length > 12) {
        error = "In-Game ID should be 8 to 12 numeric characters.";
      }
      break;

    default:
      // No specific validation for other games
      break;
  }

  return error;
};

/**
 * Get game ID format hint based on game name
 * @param {string} gameName - The name of the game from GAMES_NAMES enum
 * @returns {string} - Hint text explaining the format
 */
export const getGameIdFormatHint = gameName => {
  switch (gameName) {
    case GAMES_NAMES.LEAGUE_OF_LEGENDS:
    case GAMES_NAMES.VALORANT:
      return "(Format: RIOTID#TAGLINE)";
    case GAMES_NAMES.FORTNITE:
      return "(32 alphanumeric characters)";
    case GAMES_NAMES.FREEFIRE:
      return "(8 or 9 numeric characters)";
    case GAMES_NAMES.CALL_OF_DUTY:
      return "(19 numeric characters)";
    case GAMES_NAMES.DOTA_2:
    case GAMES_NAMES.CS_GO:
    case GAMES_NAMES.PUBG:
      return "(17 numeric characters)";
    case GAMES_NAMES.APEX_LEGENDS:
      return "(20 alphanumeric characters)";
    case GAMES_NAMES.PUBG_MOBILE:
      return "(Numeric characters only)";
    default:
      return "(Format depends on game)";
  }
};
